import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const LightboxImage = styled.img`
  max-height: 85vh;
  max-width: 80vw;
  object-fit: contain;
  user-select: none;
  pointer-events: none;

  @media (max-width: 768px) {
    max-width: 95vw;
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 24px;
  right: 24px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10001;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const NavButton = styled.button`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.$side === "left" ? "left: 24px;" : "right: 24px;")}
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10001;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: #c9a86c;
    color: #c9a86c;
  }

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    ${(props) => (props.$side === "left" ? "left: 8px;" : "right: 8px;")}
  }
`;

const Counter = styled.div`
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  color: #c9a86c;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 2px;
  z-index: 10001;
`;

const Lightbox = (props) => {
  const { images, initialIndex, onClose } = props;
  const [current, setCurrent] = useState(initialIndex ?? 0);
  const dialogRef = useRef(null);
  const restoreFocusRef = useRef(null);

  const count = images.length;

  const prev = (event) => {
    event?.stopPropagation();
    setCurrent((index) => (index - 1 + count) % count);
  };

  const next = (event) => {
    event?.stopPropagation();
    setCurrent((index) => (index + 1) % count);
  };

  /* `count` in the dependency list, and read through the setState updater rather than closed over.
   * The handler used to be registered with an empty dependency array while closing over
   * images.length, so the arrow keys permanently wrapped against the FIRST array the component
   * ever saw — opening a different gallery afterwards navigated against a stale length.
   */
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft")
        setCurrent((index) => (index - 1 + count) % count);
      if (event.key === "ArrowRight")
        setCurrent((index) => (index + 1) % count);
      if (event.key !== "Tab" || !dialogRef.current) return;

      // Focus trap: a modal that lets focus escape leaves a keyboard user tabbing an inert page
      // behind an overlay they cannot see past.
      const focusable = dialogRef.current.querySelectorAll("button, [href]");
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    restoreFocusRef.current = document.activeElement;
    document.addEventListener("keydown", onKeyDown);

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector("button")?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
      restoreFocusRef.current?.focus?.();
    };
  }, [count, onClose]);

  const source = images[current];
  const url = typeof source === "string" ? source : source?.url;
  const alt = typeof source === "string" ? "" : source?.alt_text || "";

  return (
    <Overlay
      onClick={onClose}
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={props.label}
    >
      <CloseButton
        onClick={onClose}
        type="button"
        aria-label={props.closeLabel}
      >
        <X size={22} aria-hidden="true" />
      </CloseButton>

      {count > 1 && (
        <NavButton
          $side="left"
          onClick={prev}
          type="button"
          aria-label={props.prevLabel}
        >
          <ChevronLeft size={32} aria-hidden="true" />
        </NavButton>
      )}

      <ImageWrapper onClick={(event) => event.stopPropagation()}>
        <LightboxImage src={url} alt={alt} />
      </ImageWrapper>

      {count > 1 && (
        <>
          <NavButton
            $side="right"
            onClick={next}
            type="button"
            aria-label={props.nextLabel}
          >
            <ChevronRight size={32} aria-hidden="true" />
          </NavButton>
          <Counter aria-live="polite">
            {current + 1} / {count}
          </Counter>
        </>
      )}
    </Overlay>
  );
};

export default Lightbox;
